import { Suspense, lazy, type ReactNode } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import AuthLayout from '@/layouts/AuthLayout';
import AdminLayout from '@/layouts/AdminLayout';

// Public landing
const Home = lazy(() => import('@/pages/index'));

// (auth)
const Login = lazy(() => import('@/pages/(auth)/login/index'));

// (admin) — every admin page; AdminLayout handles auth gating + sidebar.
const Dashboard = lazy(() => import('@/pages/(admin)/dashboard/index'));
const Customers = lazy(() => import('@/pages/(admin)/customers/index'));
const CustomerDetail = lazy(() => import('@/pages/(admin)/customers/[id]/index'));
const Orders = lazy(() => import('@/pages/(admin)/orders/index'));
const OrderDetail = lazy(() => import('@/pages/(admin)/orders/[id]/index'));
const OrderImport = lazy(() => import('@/pages/(admin)/orders/import/index'));
const OrderNew = lazy(() => import('@/pages/(admin)/orders/new/index'));
const Drivers = lazy(() => import('@/pages/(admin)/drivers/index'));
const Products = lazy(() => import('@/pages/(admin)/products/index'));
const Promotions = lazy(() => import('@/pages/(admin)/promotions/index'));
const Pos = lazy(() => import('@/pages/(admin)/pos/index'));
const Release = lazy(() => import('@/pages/(admin)/release/index'));
const AuditLogs = lazy(() => import('@/pages/(admin)/audit-logs/index'));
const FinoraSync = lazy(() => import('@/pages/(admin)/finora/index'));
const Staff = lazy(() => import('@/pages/(admin)/staff/index'));
const Reports = lazy(() => import('@/pages/(admin)/reports/index'));
const ReportsCustomers = lazy(() => import('@/pages/(admin)/reports/customers/index'));
const ReportsFinancial = lazy(() => import('@/pages/(admin)/reports/financial/index'));
const ReportsOperations = lazy(() => import('@/pages/(admin)/reports/operations/index'));
const ReportsOutstanding = lazy(() => import('@/pages/(admin)/reports/outstanding/index'));
const ReportsLostItems = lazy(() => import('@/pages/(admin)/reports/lost-items/index'));
const ReportsFuel = lazy(() => import('@/pages/(admin)/reports/fuel/index'));
const HardwareSettings = lazy(() => import('@/pages/(admin)/settings/hardware/index'));
const BankAccountsSettings = lazy(() => import('@/pages/(admin)/settings/bank-accounts/index'));

const PageFallback = (
  <div className="min-h-screen flex items-center justify-center text-gray-500">Loading…</div>
);

function L(node: ReactNode) {
  return <Suspense fallback={PageFallback}>{node}</Suspense>;
}

export const router = createBrowserRouter([
  // Public landing
  { path: '/', element: L(<Home />) },

  // (auth)
  {
    element: <AuthLayout />,
    children: [{ path: '/login', element: L(<Login />) }],
  },

  // Authenticated admin chrome
  {
    element: <AdminLayout />,
    children: [
      { path: '/dashboard', element: L(<Dashboard />) },
      {
        path: '/customers',
        children: [
          { index: true, element: L(<Customers />) },
          { path: ':id', element: L(<CustomerDetail />) },
        ],
      },
      {
        path: '/orders',
        children: [
          { index: true, element: L(<Orders />) },
          { path: 'new', element: L(<OrderNew />) },
          { path: 'import', element: L(<OrderImport />) },
          { path: ':id', element: L(<OrderDetail />) },
        ],
      },
      { path: '/drivers', element: L(<Drivers />) },
      { path: '/products', element: L(<Products />) },
      { path: '/promotions', element: L(<Promotions />) },
      { path: '/pos', element: L(<Pos />) },
      { path: '/release', element: L(<Release />) },
      { path: '/audit-logs', element: L(<AuditLogs />) },
      { path: '/finora', element: L(<FinoraSync />) },
      { path: '/staff', element: L(<Staff />) },
      { path: '/settings/hardware', element: L(<HardwareSettings />) },
      { path: '/settings/bank-accounts', element: L(<BankAccountsSettings />) },
      {
        path: '/reports',
        children: [
          { index: true, element: L(<Reports />) },
          { path: 'customers', element: L(<ReportsCustomers />) },
          { path: 'financial', element: L(<ReportsFinancial />) },
          { path: 'operations', element: L(<ReportsOperations />) },
          { path: 'outstanding', element: L(<ReportsOutstanding />) },
          { path: 'lost-items', element: L(<ReportsLostItems />) },
          { path: 'fuel', element: L(<ReportsFuel />) },
        ],
      },
    ],
  },

  // 404 → home
  { path: '*', element: <Navigate to="/" replace /> },
]);
